import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Lock } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../ui/dialog";

const STORAGE_KEY = "dc-camp-date";
const DEFAULT_ISO = "2026-05-21T14:00:00";
const PASSWORD = "mighty";

export const formatDateLong = (date) => {
  try {
    const d = date instanceof Date ? date : new Date(date);
    const opts = {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    };
    return d.toLocaleDateString("en-US", opts);
  } catch {
    return "—";
  }
};

export const formatTime = (date) => {
  try {
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "—";
  }
};

export const loadCampDateISO = () => {
  if (typeof window === "undefined") return DEFAULT_ISO;
  const saved = window.localStorage.getItem(STORAGE_KEY);
  return saved || DEFAULT_ISO;
};

export const saveCampDateISO = (iso) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, iso);
};

// Returns "YYYY-MM-DDTHH:mm" suitable for <input type="datetime-local">
const isoToInput = (iso) => {
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
};

export const CampDateEditor = ({ campISO, onChange }) => {
  const [open, setOpen] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [password, setPassword] = useState("");
  const [draft, setDraft] = useState(isoToInput(campISO));

  useEffect(() => {
    if (!open) {
      setUnlocked(false);
      setPassword("");
      setDraft(isoToInput(campISO));
    }
  }, [open, campISO]);

  const handleUnlock = (e) => {
    e.preventDefault();
    if (password.trim().toLowerCase() === PASSWORD) {
      setUnlocked(true);
      toast.success("Unlocked. Update the camp date and time.");
    } else {
      toast.error("Incorrect password.");
    }
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (!draft) {
      toast.error("Pick a valid date and time.");
      return;
    }
    const iso = new Date(draft).toISOString();
    saveCampDateISO(iso);
    onChange?.(iso);
    toast.success("Camp date updated.");
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          data-testid="edit-camp-date-button"
          className="inline-flex items-center gap-2 font-mono text-[10px] sm:text-[11px] tracking-[0.18em] uppercase font-bold text-[#0E0E0E] bg-[#FFD700] hover:bg-[#F4EBDC] px-3 py-2 border-2 border-[#FFD700] hover:border-[#F4EBDC] transition-colors"
        >
          <Pencil size={12} />
          Edit Camp Date
        </button>
      </DialogTrigger>
      <DialogContent
        data-testid="edit-camp-date-dialog"
        className="bg-[#F4EBDC] border-2 border-[#0E0E0E] text-[#0E0E0E] sm:max-w-md"
        style={{ borderRadius: 0, boxShadow: "8px 8px 0 #0E0E0E" }}
      >
        <DialogHeader>
          <DialogTitle className="font-display text-3xl text-[#0E0E0E]">
            {unlocked ? "EDIT CAMP DATE" : "ENTER PASSWORD"}
          </DialogTitle>
          <p className="font-mono text-[11px] tracking-[0.22em] text-[#0E0E0E]/70 mt-1">
            / DIVINE CARE · ADMIN
          </p>
        </DialogHeader>

        {!unlocked ? (
          <form onSubmit={handleUnlock} className="space-y-5 mt-4">
            <div>
              <label
                htmlFor="dc-pw"
                className="font-mono text-[11px] tracking-[0.22em] uppercase mb-2 flex items-center gap-2"
              >
                <Lock size={12} /> Password
              </label>
              <input
                id="dc-pw"
                name="password"
                type="password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="dc-field"
                placeholder="Enter admin password"
                data-testid="edit-camp-password"
              />
            </div>
            <DialogFooter className="gap-3">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="btn-base btn-ghost"
                data-testid="edit-camp-cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-base btn-ink"
                data-testid="edit-camp-unlock"
              >
                Unlock
              </button>
            </DialogFooter>
          </form>
        ) : (
          <form onSubmit={handleSave} className="space-y-5 mt-4">
            <div>
              <label
                htmlFor="dc-dt"
                className="font-mono text-[11px] tracking-[0.22em] uppercase mb-2 block"
              >
                Date &amp; Time
              </label>
              <input
                id="dc-dt"
                type="datetime-local"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                className="dc-field"
                data-testid="edit-camp-datetime"
                required
              />
              <p className="font-mono text-[10px] tracking-[0.16em] uppercase mt-2 text-[#0E0E0E]/60">
                Saved locally on this device.
              </p>
            </div>
            <DialogFooter className="gap-3">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="btn-base btn-ghost"
                data-testid="edit-camp-cancel-2"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-base btn-orange"
                data-testid="edit-camp-save"
              >
                Save Date
              </button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CampDateEditor;
